import React from 'react';
import { Card, MultiSelect, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import { useModuleAccess, useSaveModuleAccess } from '../../hooks/useTenantApi';
import { getErrorMessage } from '../../utils/api';
import { usePermission } from '../../hooks/usePermission';
import { showToast } from '../../utils/toast';

/**
 * Кто пользуется подключёнными разделами: все или только держатели ролей. Остальным раздел как
 * выключенный — без меню, напоминаний и сроков. Владелец пользуется всеми всегда.
 */
export const ModuleAccessSection: React.FC = () => {
  const { t } = useTranslation();
  const { data, isError } = useModuleAccess(usePermission('company.settings'));
  const save = useSaveModuleAccess();
  if (isError)
    return (
      <Text size="sm" c="red">
        {t('settings.moduleAccess.loadError')}
      </Text>
    );
  if (!data) return null;
  const roleOptions = data.roles.map((r) => ({ value: r._id, label: r.name }));

  const saveAccess = (key: string, roleIds: string[]) =>
    save.mutate(
      { module: key, roleIds },
      {
        onSuccess: () =>
          showToast({
            title: t('settings.moduleAccess.saved'),
            message: t(`companyModules.${key}`),
            color: 'teal',
          }),
        onError: (e) =>
          showToast({ title: t('common.error'), message: getErrorMessage(e), color: 'red' }),
      },
    );

  const isOpenToMe = (roleIds: string[]) =>
    !roleIds.length || roleIds.some((id) => data.myRoles!.includes(id));
  // Сузить открытый себе раздел до ролей без своей — значит закрыть его себе: сначала спросить.
  // Раздел уже закрыт себе — правка списка ничего не отнимает, не спрашиваем.
  const change = (key: string, roleIds: string[]) => {
    const isSelfLock =
      !data.isOwner && !!data.myRoles && isOpenToMe(data.access[key] ?? []) && !isOpenToMe(roleIds);
    if (!isSelfLock) return saveAccess(key, roleIds);
    modals.openConfirmModal({
      title: t('settings.moduleAccess.selfLockTitle'),
      centered: true,
      children: (
        <Text size="sm">
          {t('settings.moduleAccess.selfLockBody', { module: t(`companyModules.${key}`) })}
        </Text>
      ),
      labels: { confirm: t('settings.moduleAccess.selfLockConfirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => saveAccess(key, roleIds),
    });
  };

  return (
    <Card withBorder radius="md" p="lg" data-testid="module-access">
      <Stack gap="md">
        <div>
          <Title order={4}>{t('settings.moduleAccess.title')}</Title>
          <Text size="sm" c="dimmed" mt={4}>
            {t('settings.moduleAccess.hint')}
          </Text>
        </div>
        {data.modules.map((key) => (
          <MultiSelect
            key={key}
            label={t(`companyModules.${key}`)}
            placeholder={
              (data.access[key] ?? []).length ? undefined : t('settings.moduleAccess.everyone')
            }
            data={roleOptions}
            value={data.access[key] ?? []}
            onChange={(roleIds) => change(key, roleIds)}
            searchable
            disabled={save.isPending}
            clearable
          />
        ))}
      </Stack>
    </Card>
  );
};
